import { useMemo, useState } from 'react';
import type { Service, Staff, WaitlistEntry } from '@salonomy/types';
import { Button, Card, Pill } from '@salonomy/ui';
import { ConfirmModal } from '../../components/ConfirmModal.js';
import {
  useExpireWaitlistEntry,
  useOfferWaitlistEntry,
  useRemoveWaitlistEntry,
  useWaitlist,
  useWaitlistDepth,
} from '../../data/waitlist.js';
import { useAuth } from '../../lib/auth.js';
import { clockLabelAt, longDate, todayInZone } from '../../lib/time.js';
import { useToast } from '../../lib/toast.js';
import { ConvertWaitlistModal } from './ConvertWaitlistModal.js';
import { JoinWaitlistButton } from './JoinWaitlist.js';

/**
 * WS7 phase 4 — the staff waitlist surface. Two reads stacked:
 *   1. per-day DEPTH overview (how many are waiting / offered on upcoming days), and
 *   2. the active entries themselves, grouped by wanted date, with the offer/notify state visible
 *      (a cancellation auto-offers the slot to the best match; "Offered" shows that fired).
 *
 * Replaces the "Appointment waitlist" Coming-soon in SettingsPage. Sealed-book aware: the list is
 * RLS-scoped server-side (a renter sees only their own), and the Remove control is gated to mirror
 * the API's delete guard — a renter may remove only their own entries; a floor manager may remove
 * anyone's. The gate is a DISABLED state with an explanation, never a server 403.
 */

export function WaitlistPanel({
  staff,
  services,
  tz,
}: {
  staff: Staff[];
  services: Service[];
  tz: string;
}) {
  const { user, isManager } = useAuth();
  const today = todayInZone(tz);
  // Active entries (waiting + offered) — the API default when status is omitted.
  const { data: entries = [], isLoading } = useWaitlist();
  const { data: depthRows = [] } = useWaitlistDepth();
  const remove = useRemoveWaitlistEntry();
  const offer = useOfferWaitlistEntry();
  const expire = useExpireWaitlistEntry();
  const toast = useToast();

  const [toRemove, setToRemove] = useState<WaitlistEntry | null>(null);
  // WS10(a) manual-action targets: which WAITING entry to offer, which OFFERED entry to convert
  // (opens the time-picker dialog) or to decline (→ expire, since the enum has no 'declined').
  const [toOffer, setToOffer] = useState<WaitlistEntry | null>(null);
  const [toConvert, setToConvert] = useState<WaitlistEntry | null>(null);
  const [toDecline, setToDecline] = useState<WaitlistEntry | null>(null);

  const serviceName = useMemo(() => {
    const m = new Map(services.map((s) => [s.id, s.name]));
    return (id: string) => m.get(id) ?? 'Service';
  }, [services]);
  const staffName = useMemo(() => {
    const m = new Map(staff.map((s) => [s.id, s.displayName]));
    return (id: string | null) => (id ? (m.get(id) ?? 'Stylist') : 'Any stylist');
  }, [staff]);

  // Group entries by wanted date (upcoming first), ignoring days already past.
  const byDate = useMemo(() => {
    const groups = new Map<string, WaitlistEntry[]>();
    for (const e of entries) {
      if (e.wantedDate < today) continue;
      const list = groups.get(e.wantedDate) ?? [];
      list.push(e);
      groups.set(e.wantedDate, list);
    }
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [entries, today]);

  // Upcoming depth (today onward, anything non-empty) for the at-a-glance overview row.
  const upcomingDepth = useMemo(
    () =>
      depthRows
        .filter((d) => d.wantedDate >= today && (d.waiting > 0 || d.offered > 0))
        .sort((a, b) => a.wantedDate.localeCompare(b.wantedDate)),
    [depthRows, today],
  );

  // A renter may only act on their OWN entries; a floor manager may act on anyone's. This is the
  // single RBAC predicate the API applies to every waitlist action (remove/offer/convert/expire —
  // assertCanManageEntry in waitlist/routes.ts), so all four controls gate on it: a non-permitted
  // user gets a disabled state, never a 403 as first feedback.
  function canManage(e: WaitlistEntry): boolean {
    if (isManager) return true;
    // Non-manager: mirror the server's assertCanManageEntry EXACTLY (waitlist/service.ts) — it blocks
    // only a SEALED entry (renterId set) belonging to ANOTHER renter. So a renter may act on a FLOOR
    // entry (null renterId, shared/owner book) as well as their own sealed entries; only another
    // renter's sealed entry is off-limits.
    return e.renterId === null || e.renterId === (user?.renterId ?? null);
  }

  async function confirmRemove() {
    if (!toRemove) return;
    try {
      await remove.mutateAsync(toRemove.id);
      toast.success('Removed from the waitlist');
      setToRemove(null);
    } catch {
      toast.error('Could not remove this waitlist entry.');
    }
  }

  // Offer a WAITING entry its slot via the gap-offer comms path. The result tells us whether a text
  // actually went out (a salon paused at its messaging cap is still offered, but notified:false — we
  // never auto-spend), so we reflect that honestly rather than implying a text always sends.
  async function confirmOffer() {
    if (!toOffer) return;
    const hadPhone = Boolean(toOffer.phone);
    try {
      const result = await offer.mutateAsync(toOffer.id);
      toast.success(
        result.notified
          ? `Offered the slot to ${toOffer.clientName} — texted.`
          : hadPhone
            ? `Offered to ${toOffer.clientName} — text paused (messaging at its cap).`
            : `Offered to ${toOffer.clientName} — no number to text.`,
      );
      setToOffer(null);
    } catch {
      toast.error('Could not offer this slot.');
    }
  }

  // Decline an OFFERED entry. The status enum has no 'declined' — a decline IS an expire: the offer
  // is released and the API re-offers the next-best match, which we surface if it fired.
  async function confirmDecline() {
    if (!toDecline) return;
    try {
      const { reoffer } = await expire.mutateAsync(toDecline.id);
      toast.success(
        reoffer?.matched
          ? `Declined — re-offered to ${reoffer.matched.clientName}.`
          : 'Declined — slot released.',
      );
      setToDecline(null);
    } catch {
      toast.error('Could not decline this offer.');
    }
  }

  const notPermittedTitle =
    'This entry is in another book — only its owner or a manager can act on it.';

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div className="section-title">Appointment waitlist</div>
          <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
            When a day’s full, clients wait here for a date, stylist or service. A cancellation
            offers the freed slot to the best match — by text if we have their number.
          </div>
        </div>
        <JoinWaitlistButton staff={staff} services={services} tz={tz} />
      </div>

      {/* ── Per-day depth overview ── */}
      {upcomingDepth.length > 0 ? (
        <div
          style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}
          aria-label="Waitlist depth by day"
        >
          {upcomingDepth.map((d) => (
            <Pill key={d.wantedDate} tone={d.offered > 0 ? 'cobalt' : 'soft'} className="mono">
              {longDate(d.wantedDate, tz)} · {d.waiting} waiting
              {d.offered > 0 ? ` · ${d.offered} offered` : ''}
            </Pill>
          ))}
        </div>
      ) : null}

      {/* ── Grouped entry list ── */}
      {isLoading ? (
        <div className="muted" style={{ fontSize: 13, padding: '12px 0 0' }}>
          Loading the waitlist…
        </div>
      ) : byDate.length === 0 ? (
        <div className="muted" style={{ fontSize: 13, padding: '12px 0 0' }}>
          No one’s on the waitlist. Add a client when a day they want is full.
        </div>
      ) : (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {byDate.map(([date, list]) => (
            <div key={date}>
              <div
                className="mono"
                style={{ fontSize: 12.5, color: 'var(--slm-muted)', marginBottom: 6 }}
              >
                {longDate(date, tz)} · {list.length} waiting
              </div>
              <div className="settings-list">
                {list.map((e) => {
                  const allowed = canManage(e);
                  // While any action is in flight we lock this entry's controls so a double-tap
                  // can't fire twice (offer/expire/remove key on the entry id; convert is gated in
                  // its own dialog). isPending is mutation-wide, which is fine here — a panel rarely
                  // acts on two entries at once, and the lock errs safe.
                  const busy = remove.isPending || offer.isPending || expire.isPending;
                  return (
                    <div key={e.id} className="settings-row">
                      <div>
                        <div
                          style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}
                        >
                          {e.clientName}
                          {e.status === 'offered' ? (
                            <Pill tone="cobalt" className="mono">
                              {e.phone ? 'Slot offered' : 'Offered (no number)'}
                            </Pill>
                          ) : null}
                        </div>
                        <div className="muted" style={{ fontSize: 12.5 }}>
                          {serviceName(e.serviceId)} · {staffName(e.staffId)}
                          {e.renterId ? ' · sealed' : ''}
                          {!e.phone ? ' · no number' : ''}
                          {e.status === 'offered' && e.offeredAt
                            ? ` · offered ${clockLabelAt(e.offeredAt, tz)}`
                            : ''}
                          {e.note ? ` · ${e.note}` : ''}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {/* WAITING → offer the slot (the manual twin of the auto-offer on cancel). */}
                        {e.status === 'waiting' ? (
                          <Button
                            size="sm"
                            variant="soft"
                            disabled={!allowed || busy}
                            title={allowed ? undefined : notPermittedTitle}
                            onClick={() => setToOffer(e)}
                          >
                            Offer slot
                          </Button>
                        ) : null}
                        {/* OFFERED → convert to a booking (time-picker dialog), or decline (→ expire). */}
                        {e.status === 'offered' ? (
                          <>
                            <Button
                              size="sm"
                              variant="primary"
                              disabled={!allowed || busy}
                              title={allowed ? undefined : notPermittedTitle}
                              onClick={() => setToConvert(e)}
                            >
                              Book it
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={!allowed || busy}
                              title={allowed ? undefined : notPermittedTitle}
                              onClick={() => setToDecline(e)}
                            >
                              Decline
                            </Button>
                          </>
                        ) : null}
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={!allowed || busy}
                          title={allowed ? undefined : notPermittedTitle}
                          onClick={() => setToRemove(e)}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmModal
        open={Boolean(toRemove)}
        title="Remove from waitlist"
        confirmLabel="Remove"
        busy={remove.isPending}
        onConfirm={confirmRemove}
        onClose={() => setToRemove(null)}
      >
        <p style={{ margin: 0 }}>
          Remove <strong>{toRemove?.clientName}</strong> from the waitlist for{' '}
          {toRemove ? longDate(toRemove.wantedDate, tz) : ''}? They won’t be offered a slot if one
          frees up.
        </p>
      </ConfirmModal>

      {/* Offer a waiting entry its slot — honest about whether a text will actually go out. */}
      <ConfirmModal
        open={Boolean(toOffer)}
        title="Offer this slot"
        confirmLabel="Offer slot"
        busy={offer.isPending}
        onConfirm={confirmOffer}
        onClose={() => setToOffer(null)}
      >
        <p style={{ margin: 0 }}>
          Offer the freed slot to <strong>{toOffer?.clientName}</strong> for{' '}
          {toOffer ? longDate(toOffer.wantedDate, tz) : ''}?{' '}
          {toOffer?.phone
            ? 'We’ll text them the offer (unless messaging is paused at its cap).'
            : 'They have no number on file, so we can’t text — you’ll need to reach them another way.'}
        </p>
      </ConfirmModal>

      {/* Decline maps to expire (no 'declined' state): release the offer + re-offer the next match. */}
      <ConfirmModal
        open={Boolean(toDecline)}
        title="Decline this offer"
        confirmLabel="Decline"
        busy={expire.isPending}
        onConfirm={confirmDecline}
        onClose={() => setToDecline(null)}
      >
        <p style={{ margin: 0 }}>
          Mark <strong>{toDecline?.clientName}</strong>’s offer as declined? The slot is released
          and we’ll offer it to the next-best match on the waitlist.
        </p>
      </ConfirmModal>

      {/* Book it — the small time-picker dialog that converts an offered entry into an appointment. */}
      {toConvert ? (
        <ConvertWaitlistModal
          entry={toConvert}
          serviceName={serviceName}
          staffName={staffName}
          tz={tz}
          onClose={() => setToConvert(null)}
        />
      ) : null}
    </Card>
  );
}
